import apiClient, { type ApiError } from './apiClient'
import { isAuthError, createAuthError, extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from './config'
import { getStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'
import type { UserData } from '@/types/auth'

/**
 * Unwrap `/api/verify` body whether the API returns `UserData` or `{ user_data: UserData }`.
 * Impersonation and other call sites must match `verifyUser()` so Redux gets a real `current_role`.
 */
export function parseVerifyUserResponse(responseData: unknown): UserData {
  if (responseData && typeof responseData === 'object') {
    if ('user_data' in responseData) {
      return (responseData as { user_data: UserData }).user_data
    }
    return responseData as UserData
  }
  throw {
    message: 'Invalid response format from verify endpoint',
    status: 500,
  } as ApiError
}

/**
 * Profile update request payload
 */
export interface UpdateProfileRequest {
  first_name: string
  last_name: string
  mobile_no: string
  email?: string
  job_title?: string
  received_notification_email?: string
  show_tab_indication?: string
}

/**
 * Verify user session and refresh user data
 * @param force - Force refresh user data
 * @returns Updated user data
 * @throws ApiError if request fails
 */
export async function verifyUser(force: boolean = false): Promise<UserData> {
  try {
    const url = force ? `${API_ENDPOINTS.VERIFY_USER}?force` : API_ENDPOINTS.VERIFY_USER
    const response = await apiClient.get<{ user_data: UserData } | UserData>(url)

    return parseVerifyUserResponse(response.data)
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to verify user session.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Update user profile
 * @param profileData - Profile data to update
 * @returns Success response
 * @throws ApiError if request fails
 */
export async function updateProfile(profileData: UpdateProfileRequest): Promise<void> {
  try {
    // Get token for additional header
    const token = getStorageItem(STORAGE_KEYS.authToken)

    await apiClient.post(
      API_ENDPOINTS.PROFILE,
      profileData,
      {
        headers: {
          'Accept-Language': 'en-US,en;q=0.9',
          ...(token && { token }), // Additional token header as per API docs
        },
      }
    )
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to update profile.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Change password request payload
 */
export interface ChangePasswordRequest {
  old_password: string
  new_password: string
  confirm_password: string
}

/**
 * Change user password
 * @param passwordData - Password change data
 * @returns Success response
 * @throws ApiError if request fails
 */
export async function changePassword(passwordData: ChangePasswordRequest): Promise<void> {
  try {
    // Get token for additional header
    const token = getStorageItem(STORAGE_KEYS.authToken)

    await apiClient.post(
      API_ENDPOINTS.PASSWORD,
      passwordData,
      {
        headers: {
          'Accept-Language': 'en-US,en;q=0.9',
          ...(token && { token }), // Additional token header as per API docs
        },
      }
    )
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to change password.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

